/**
 * petz-brand.js — the single shared catalogue facet enhancement.
 *
 * Despite the historical filename, this asset is the one runtime owner of facet
 * behaviour for ALL THREE catalogue families: Standard Category, Brand
 * Collection and Search. They render the same snippets/collection-facets.liquid
 * form, so no family owns a copy.
 *
 * Owns, all generically and all bound by contract rather than by family:
 *   - mobile filter drawer  — every dialog.petz-filter-drawer, trigger resolved
 *     through aria-controls; native showModal(), Escape closes, backdrop click
 *     closes, focus returns to the trigger
 *   - long-list disclosure  — every [data-petz-facet-more]
 *   - facet group disclosure — every .petz-facet__toggle
 *   - checkbox auto-submit  — every form.petz-facets-form
 *
 * Progressive enhancement only. Filtering itself is Shopify-native: navigation
 * uses Shopify's own FilterValue URLs, Products are server-rendered Liquid, and
 * the native Apply submit stays functional with JavaScript disabled. There is
 * no Storefront API result engine, no client-side Product filtering and no
 * Load-more path. The disclosures perform no form submit, no URL change and no
 * Product request, so checkbox state is never lost.
 */

(function () {
  'use strict';

  function initBrandFilterDrawers() {
    document.querySelectorAll('dialog.petz-filter-drawer').forEach(function (drawer) {
      var drawerId = drawer.id;
      if (!drawerId) return;

      var trigger = document.querySelector(
        '[aria-controls="' + drawerId + '"]'
      );
      if (!trigger) return;

      /* ── Open ─────────────────────────────────────────────────────────── */
      trigger.addEventListener('click', function () {
        drawer.showModal();
        trigger.setAttribute('aria-expanded', 'true');
      });

      /* ── Close (X button) ─────────────────────────────────────────────── */
      var closeBtn = drawer.querySelector('.petz-filter-drawer__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          drawer.close();
        });
      }

      /* ── Backdrop click (click falls through to <dialog> element itself) */
      drawer.addEventListener('click', function (e) {
        if (e.target === drawer) {
          drawer.close();
        }
      });

      /* ── On close: reset aria-expanded, return focus to trigger ──────── */
      drawer.addEventListener('close', function () {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      });
    });
  }

  /* ── Long-list facet disclosure ──────────────────────────────────────────
     Reveals the values hidden past the global filter_values_initial_limit
     threshold. Active values are never hidden by the renderer, so they stay
     visible in the compact state.                                           */
  function initFacetDisclosures() {
    document.querySelectorAll('[data-petz-facet-more]').forEach(function (btn) {
      if (btn.dataset.petzFacetBound === '1') return;
      btn.dataset.petzFacetBound = '1';

      btn.addEventListener('click', function () {
        var listId = btn.getAttribute('aria-controls');
        var list = listId ? document.getElementById(listId) : null;
        if (!list) return;

        var expanded = btn.getAttribute('aria-expanded') === 'true';
        list.querySelectorAll('[data-petz-facet-extra]').forEach(function (item) {
          item.hidden = expanded;
        });

        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var next = expanded ? btn.dataset.moreLabel : btn.dataset.lessLabel;
        if (next) btn.textContent = next;
      });
    });
  }

  /* ── Facet group expand / collapse ───────────────────────────────────────
     Generic disclosure for every facet group heading on every catalogue
     family. Binds `.petz-facet__toggle`, resolves its panel through
     `aria-controls`, and keeps `aria-expanded` and `panel.hidden` in sync.
     Native <button> semantics give Enter and Space for free.                */
  function initFacetGroupToggles() {
    document.querySelectorAll('.petz-facet__toggle').forEach(function (btn) {
      if (btn.dataset.petzFacetToggleBound === '1') return;

      var panelId = btn.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      btn.dataset.petzFacetToggleBound = '1';

      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.hidden = expanded;
      });
    });
  }

  /* ── Facet auto-submit ───────────────────────────────────────────────────
     Progressive enhancement for list/boolean choices on every catalogue
     family — Category, Brand and Search all render the same
     snippets/collection-facets.liquid form, so one generic initializer serves
     all three and no family owns its own copy.

     Navigation uses Shopify's own FilterValue URLs (`data-url-add` /
     `data-url-remove`) — never a reconstructed query string. The results
     fragment only returns the shopper to the Product-results region; the
     filter query itself stays Shopify-owned.

     Price inputs are deliberately excluded: price stays a considered
     submission through its own `Update price` control.

     The general Apply submit is hidden only after this binding succeeds for
     that form, so the no-JavaScript path keeps a working control.           */
  function initFacetAutoSubmit() {
    document.querySelectorAll('form.petz-facets-form').forEach(function (form) {
      if (form.dataset.petzAutoSubmitBound === '1') return;

      var boxes = form.querySelectorAll('input.petz-facet__checkbox');
      if (!boxes.length) return;

      var target = form.getAttribute('data-petz-results-target');
      var fragment = target ? '#' + target : '';

      boxes.forEach(function (cb) {
        cb.addEventListener('change', function () {
          var url = this.checked ? this.dataset.urlAdd : this.dataset.urlRemove;
          if (!url) return;
          window.location.href = fragment && url.indexOf('#') === -1 ? url + fragment : url;
        });
      });

      form.dataset.petzAutoSubmitBound = '1';

      var apply = form.querySelector('.petz-facet__apply');
      if (apply) apply.hidden = true;
    });
  }

  function initAll() {
    initBrandFilterDrawers();
    initFacetDisclosures();
    initFacetGroupToggles();
    initFacetAutoSubmit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-initialise when the editor dynamically inserts the section. */
  document.addEventListener('shopify:section:load', function (e) {
    if (
      e.target.querySelector('dialog.petz-filter-drawer') ||
      e.target.querySelector('[data-petz-facet-more]') ||
      e.target.querySelector('.petz-facet__toggle') ||
      e.target.querySelector('form.petz-facets-form')
    ) {
      initAll();
    }
  });
})();
